import { useEffect, useState } from "react";
import styled from "styled-components";
import { COLORS, FONTSIZE, FONTWEIGHT } from "helpers/designConstants";

// Re-tick interval so "X min ago" stays honest without a reload. 30s is
// plenty — the scanner runs on a daily cron and client auto-refresh runs
// every few minutes.
const TICK_MS = 30_000;

function formatRelative(iso) {
  if (!iso) return null;
  const now = Date.now();
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const deltaSec = Math.max(0, Math.round((now - then) / 1000));

  if (deltaSec < 45) return "just now";
  if (deltaSec < 90) return "1 min ago";
  const deltaMin = Math.round(deltaSec / 60);
  if (deltaMin < 60) return `${deltaMin} min ago`;
  const deltaHr = Math.round(deltaMin / 60);
  if (deltaHr < 24) return `${deltaHr} hr ago`;
  const deltaDay = Math.round(deltaHr / 24);
  if (deltaDay === 1) return "yesterday";
  if (deltaDay < 7) return `${deltaDay} days ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Displays "scanned X ago" derived from the latest last_seen_at on loaded
 * issues. Pure display — no reload button. The dashboard refreshes itself
 * automatically on tab focus and on a slow background interval (see
 * useStockIssues), so manual reload was more confusing than useful.
 */
export default function ScanFreshness({ lastScanAt }) {
  const [, forceTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const label = formatRelative(lastScanAt);

  return (
    <ScanLabel>
      {label ? (
        <>
          <ScanWord>scanned</ScanWord>
          <ScanTime title={lastScanAt || ""}>{label}</ScanTime>
        </>
      ) : (
        <ScanWord>no scan yet</ScanWord>
      )}
    </ScanLabel>
  );
}

const ScanLabel = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  font-size: ${FONTSIZE.xsmall};
  color: ${COLORS.stormtechGrey};
  text-transform: lowercase;
  letter-spacing: 0.02em;
`;

const ScanWord = styled.span`
  color: ${COLORS.stormtechGrey};
`;

const ScanTime = styled.span`
  color: ${COLORS.text};
  font-weight: ${FONTWEIGHT.heavy};
`;
